import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader
} from "@velaro/velaro-component-lib";
import * as React from "react";
import {
  BubbleInvitation,
  BannerInvitation,
  PopoverInvitation,
  Invitation,
  invitationTypeDefs
} from "./types";
import {
  PrimaryBlueButton,
  SecondaryBlueButton
} from "@velaro/velaro-component-lib";
import { useState } from "react";
import BaseInvitationEditor from "./BaseInvitationEditor";
import useApi from "@/hooks/useApi";
import AuthService from "@/services/AuthService";
import { ExpressionEditor } from "@/components/Expressions/ExpressionEditor";
import { Expression } from "@/components/Expressions/types";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import { FormGroup } from "../Workflows/RightAside/RightAsideHelpers";
import Input from "@/components/Input";

interface Props {
  onUpdate(invitation: Invitation): void;
  onClose(): void;
  invitation?: Invitation;
}

interface QuickstartTemplate {
  name: string;
  description: string;
  invitation: Record<string, unknown>;
}

const QUICKSTART_TEMPLATES: QuickstartTemplate[] = [
  {
    name: "Welcome first-time visitors",
    description: "Bubble • 10s on page • first-time visitor",
    invitation: {
      type: "bubble",
      messages: ["Hi there! Welcome to our site — can I help you find something?"],
      messageDelaySeconds: 3,
      triggerExpression: [
        [
          { type: "timeOnPage", data: '{"seconds":10}' },
          { type: "returnVisitor", data: '{"isReturn":false}' }
        ]
      ],
      resetFrequency: "Daily"
    }
  },
  {
    name: "Recover exit intent",
    description: "Popover • exit intent • pricing page",
    invitation: {
      type: "popover",
      messages: [
        "Before you go — have a question about pricing? We'd love to help!"
      ],
      messageDelaySeconds: 0,
      ctaLabel: "Chat with us",
      triggerExpression: [
        [
          { type: "exitIntent", data: '{}' },
          { type: "url", data: '{"comparator":"contains","value":"pricing"}' }
        ]
      ],
      resetFrequency: "Daily"
    }
  },
  {
    name: "High-value cart assist",
    description: "Popover • exit intent",
    invitation: {
      type: "popover",
      messages: [
        "Need help with your order? Our team can answer any questions."
      ],
      messageDelaySeconds: 0,
      ctaLabel: "Get help",
      triggerExpression: [[{ type: "exitIntent", data: '{}' }]],
      resetFrequency: "Hourly"
    }
  },
  {
    name: "Return visitor re-engage",
    description: "Bubble • 5s on page • returning visitor",
    invitation: {
      type: "bubble",
      messages: ["Welcome back! Can we help you with anything today?"],
      messageDelaySeconds: 3,
      triggerExpression: [
        [
          { type: "timeOnPage", data: '{"seconds":5}' },
          { type: "returnVisitor", data: '{"isReturn":true}' }
        ]
      ],
      resetFrequency: "Weekly"
    }
  },
  {
    name: "Blog reader to demo",
    description: "Banner • 75% scroll depth • blog pages",
    invitation: {
      type: "banner",
      message: "Enjoyed this article? See how Velaro can help your team.",
      position: "bottom",
      ctaLabel: "Get a demo",
      triggerExpression: [
        [
          { type: "scrollDepth", data: '{"percent":75}' },
          { type: "url", data: '{"comparator":"contains","value":"/blog"}' }
        ]
      ],
      resetFrequency: "Weekly"
    }
  },
  {
    name: "Pricing page intent",
    description: "Popover • 20s on pricing page",
    invitation: {
      type: "popover",
      messages: [
        "Comparing plans? We can walk you through the differences."
      ],
      messageDelaySeconds: 0,
      ctaLabel: "Talk to sales",
      triggerExpression: [
        [
          { type: "timeOnPage", data: '{"seconds":20}' },
          { type: "url", data: '{"comparator":"contains","value":"pricing"}' }
        ]
      ],
      resetFrequency: "Daily"
    }
  }
];

function getDefaultInvitationModel(): BubbleInvitation {
  return {
    siteId: AuthService.profile.siteId,
    type: "bubble",
    name: "New Invitation",
    description: "Engages visitors with a friendly message",
    triggerExpression: [[{ type: "timeOnPage", data: '{"seconds":10}' }]],
    messageDelaySeconds: 3,
    resetFrequency: "Never",
    messages: ["Hello!"]
  };
}

export default function InvitationModal({
  invitation,
  onUpdate,
  onClose
}: Props) {
  const [workingModel, setWorkingModel] = useState<Invitation>(
    invitation || getDefaultInvitationModel()
  );

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [showQuickstarts, setShowQuickstarts] = useState(false);
  const api = useApi();
  const { displayToast } = useToastAlert();
  const SubtypeEditor = invitationTypeDefs.find(
    (t) => t.value === workingModel.type
  )?.editor;

  const isNew = !workingModel.id;
  const buttonLabel = isNew
    ? saving
      ? "Creating"
      : "Create"
    : saving
    ? "Saving"
    : "Save";

  function validate() {
    const errors: Record<string, string> = {};
    if (!workingModel.type) {
      errors["type"] = "Type is required";
    }

    if (!workingModel.name) {
      errors["name"] = "A name is required";
    }

    if (workingModel.type === "bubble") {
      const bubble = workingModel as BubbleInvitation;
      if (bubble.messages.length === 0) {
        errors["messages"] = "At least one message is required";
      }

      if (bubble.messageDelaySeconds < 0) {
        errors["messageDelaySeconds"] =
          "Message delay must be a positive number";
      }
    }

    if (workingModel.type === "popover") {
      const popover = workingModel as PopoverInvitation;
      if (!Array.isArray(popover.messages) || popover.messages.length === 0) {
        errors["messages"] = "At least one message is required";
      }
    }

    if (workingModel.type === "banner") {
      const banner = workingModel as BannerInvitation;
      if (!banner.message) {
        errors["message"] = "A message is required";
      }
    }

    return errors;
  }

  const save = async () => {
    const errors = validate();
    setErrors(errors);
    const hasErrors = Object.keys(errors).length > 0;
    if (hasErrors) {
      return;
    }

    setSaving(true);
    const response = await api.messaging.post("Invitations", workingModel);

    if (response.ok) {
      displayToast(Severity.Success, "Invitation saved successfully.");
      onUpdate(await response.json());
    } else {
      const error = await response.json();
      displayToast(Severity.Error, JSON.stringify(error));
    }
    onClose();
    setSaving(false);
  };

  return (
    <Modal show={true} maxWidth="max-w-[1000px]">
      <ModalHeader
        title={isNew ? "Create Invitation" : "Edit Invitation"}
        onClose={onClose}
      />
      <ModalBody>
        <div className="flex gap-4">
          <div className="p-1 w-1/2 max-h-[700px] overflow-auto">
            {/* Quickstart templates */}
            <div className="mb-4 border border-blue-100 rounded-lg bg-blue-50 p-3">
              <button
                type="button"
                onClick={() => setShowQuickstarts((prev) => !prev)}
                className="flex items-center gap-2 w-full text-left text-sm font-medium text-blue-700"
              >
                <span>Quickstart templates</span>
                <span className="ml-auto text-xs">
                  {showQuickstarts ? "▲" : "▼"}
                </span>
              </button>
              {showQuickstarts && (
                <div className="mt-3 grid grid-cols-1 gap-2">
                  {QUICKSTART_TEMPLATES.map((tpl) => (
                    <button
                      key={tpl.name}
                      type="button"
                      onClick={() => {
                        setWorkingModel((prev) => ({
                          ...prev,
                          ...(tpl.invitation as Partial<Invitation>),
                          name: tpl.name,
                          description: tpl.description
                        } as Invitation));
                        setShowQuickstarts(false);
                      }}
                      className="text-left text-xs border border-blue-200 bg-white rounded p-2 hover:bg-blue-50"
                    >
                      <div className="font-medium text-gray-800">{tpl.name}</div>
                      <div className="text-gray-500 mt-0.5">
                        {tpl.description}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <BaseInvitationEditor
              invitation={workingModel}
              errors={errors}
              onUpdate={setWorkingModel}
            />

            {/* Department routing */}
            <FormGroup
              label="Route to Department"
              tooltipText="Visitors who accept this invitation will be routed to this team"
            >
              {/* TODO: Replace with TeamSelect component when available */}
              <Input
                type="number"
                value={
                  workingModel.routeToTeamId !== undefined
                    ? String(workingModel.routeToTeamId)
                    : ""
                }
                onChange={(value) =>
                  setWorkingModel((prev) => ({
                    ...prev,
                    routeToTeamId: value ? parseInt(value) : undefined
                  }))
                }
              />
            </FormGroup>

            {SubtypeEditor && (
              <SubtypeEditor
                invitation={workingModel}
                errors={errors}
                onUpdate={setWorkingModel}
              />
            )}
          </div>
          <div className="border border-y border-slate-gray-50"></div>
          <div className="p-1 w-1/2 max-h-[700px] overflow-auto">
            <ExpressionEditor
              expressionType="send-invite"
              expression={workingModel.triggerExpression}
              onUpdate={(triggerExpression: Expression) => {
                setWorkingModel({
                  ...workingModel,
                  triggerExpression
                });
              }}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="flex gap-2 justify-end py-4">
          <SecondaryBlueButton label="Close" onClick={onClose} />
          <PrimaryBlueButton
            disabled={saving}
            label={buttonLabel}
            onClick={save}
          />
        </div>
      </ModalFooter>
    </Modal>
  );
}
